<script lang="ts">
  import Earth from '~icons/mdi/earth';
  import CalendarBadge from '~icons/mdi/calendar-badge';
  import Shimmer from '~icons/mdi/shimmer';
  import Spinner from '~/components/Spinner.svelte';
  import BarChart from '~/charts/BarChart.svelte';
  import {
    dailyChartQuery,
    winStreakQuery,
    type StatsMode,
    isPremium,
    getChart,
    getStreak,
  } from '~/lib/queries.svelte';
  import { Tab, Tabs } from '~/components/tabs';
  import TabList from '~/components/tabs/TabList.svelte';
  import type Icon from '~icons/mdi/calendar';
  import Premium from '~/icons/Premium.svelte';
  import Stat from './Stat.svelte';
  import { derived as _derived } from 'svelte/store';
  import { goto } from '$app/navigation';
  import { page } from '$app/state';
  import { getGame } from '~/store/GameContext.svelte';
  import { DailyGame } from '~/store/daily.svelte';
  import { PersistedState } from 'runed';

  const game = $derived(getGame<DailyGame>().current);

  type Props = {
    isLatestDaily?: boolean;
  };
  const props: Props = $props();

  type TabT = {
    desc: string;
    icon: typeof Icon | typeof Premium;
  };

  const premium = isPremium();

  let tabs: Record<StatsMode, TabT> = $derived({
    first: {
      desc: 'First Attempt',
      icon: CalendarBadge,
    },
    best: {
      desc: 'Best Attempt',
      icon: premium.data ? Shimmer : Premium,
    },
    global: {
      desc: 'Global',
      icon: premium.data ? Earth : Premium,
    },
  });

  let selected = new PersistedState<StatsMode>('daily-stats-tab', 'first');

  $effect(() => {
    if (selected.current !== 'first') {
      pushPremium();
    }
  });

  const pushPremium = async () => {
    if (!premium.data) {
      selected.current = 'first';
      await goto('?premium=t#stats', { state: page.state });
    }
  };

  const serverStreak = $derived(winStreakQuery({
    mode: game.variant,
    enabled: !!premium.data,
  }));
  const serverChart = $derived(
    dailyChartQuery({
      gameId: game.id,
      mode: game.variant,
      stat: selected.current,
    }),
  );
</script>

{#await game.getStats() then localStats}
  {@const streak = serverStreak.data ?? getStreak(localStats)}
  {#if premium.data || props.isLatestDaily}
    <div class="stats">
      <Stat value={streak.currStreak ?? '-'} label="Streak" delay={0} />
      <Stat value={streak.bestStreak ?? '-'} label="Best" delay={200} />
      <Stat
        value={streak.winRate ? Math.round(100 * streak.winRate) + '%' : '-'}
        label="Win Rate"
        delay={400}
      />
    </div>
    <div class="header">
      <h2 class="main">Star Distribution</h2>
      <div class="header-text">
        <Tabs key="stats-tab" bind:selected={selected}>
          <TabList>
            {#each Object.entries(tabs) as [name, value]}
              <Tab {name}>
                <value.icon size={20} />
                {value.desc}
              </Tab>
            {/each}
          </TabList>
        </Tabs>
      </div>
    </div>
    {@const chart = serverChart.data ?? getChart(localStats.stars)}
    <div class="chart-container">
      {#if serverChart.isFetching}
        <div class="spinner">
          <Spinner />
        </div>
      {/if}
      <BarChart stars={chart ?? []} />
    </div>
  {:else}
    <!-- TODO better text -->
    <div>replaying puzzles does not change your daily stats</div>
  {/if}
{/await}

<style lang="scss">
  @use '~/theme/include-media' as *;

  .header {
    margin-top: 1em;
    margin-bottom: 1em;
    & .header-text {
      overflow: scroll;
      overflow-x: auto;
      overflow-y: hidden;
      grid-column: 2;
    }
    & .main {
      margin: 0;
    }
  }
  .stats {
    display: grid;
    grid-auto-flow: column;
    justify-content: center;
    gap: 3em;
    @include media('<=tablet') {
      gap: 2em;
    }
  }
  .chart-container {
    position: relative;
    overflow: hidden;
    .spinner {
      position: absolute;
      z-index: 2;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
</style>
